import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';
import { BreakDuration } from '../../../types/state/timecards';
import { formatDurationStartEndDate } from '../../../utils/employeeTimeCardhelper';
import { toast } from 'react-toastify';
import { setTopNavNameTitle } from '../../../redux/features/mobileLayout';
import { useAppDispatch } from '../../../redux/store';

interface LocationState {
  breaks: BreakDuration[];
  isHoursEntry?: boolean;
  entryStartDate?: string;
  entryEndDate?: string;
  returnPath: string;
}

const BreakManagerPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const state = location.state as LocationState & { editIndex?: number };
  
  // Check if we have valid state, if not redirect back to timesheet entry
  useEffect(() => {
    if (!state) {
      console.error("BreakManagerPage accessed without proper state data");
      navigate("/m-add-timesheet-entry");
    }
  }, [state, navigate]);
  
  const { breaks: initialBreaks, isHoursEntry = false, entryStartDate = '', entryEndDate = '', returnPath = '/m-add-timesheet-entry', editIndex } = state || {};
  const [breakHasChanged, setBreakHasChanged] = useState(false);
  
  const [breaks, setBreaks] = useState<BreakDuration[]>(initialBreaks || []);
  // Always show the form directly - no intermediate page
  const [newBreak, setNewBreak] = useState<BreakDuration>({
    type: 'unpaid',
    duration_minutes: 30
  });
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  
  // Use string-based state for date/time inputs (like AddTimesheetEntry)
  const [startDateTime, setStartDateTime] = useState<string>("");
  const [endDateTime, setEndDateTime] = useState<string>("");
  
  // Toggle between duration input modes - true for start/end time, false for direct duration input
  const [isTimeInputMode, setIsTimeInputMode] = useState<boolean>(true);

  useEffect(() => {
    // Set title based on whether we're editing or adding
    const title = (editIndex !== undefined) ? "Edit Break" : "Add Break";
    dispatch(setTopNavNameTitle(title));
    
    // If an edit index was provided, load that break's data
    if (editIndex !== undefined && initialBreaks && initialBreaks[editIndex]) {
      setNewBreak({ ...initialBreaks[editIndex] });
      setEditingIndex(editIndex);
      
      // Calculate end time based on start time + duration
      const now = new Date();
      const duration = initialBreaks[editIndex].duration_minutes || 30;
      const later = new Date(now.getTime() + duration * 60000);
      
      setStartDateTime(now.toISOString().slice(0, 16));
      setEndDateTime(later.toISOString().slice(0, 16));
    }
    
    // Handle browser back button
    const handlePopState = (e: PopStateEvent) => {
      if (breakHasChanged) {
        e.preventDefault();
        if (window.confirm("You have unsaved changes. Are you sure you want to go back without saving?")) {
          navigate(returnPath);
        }
        return;
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [dispatch, breakHasChanged, navigate, returnPath, initialBreaks, editIndex]);

  const validateBreak = (breakData: BreakDuration): { valid: boolean; message?: string } => {
    // Validate duration
    if (breakData.duration_minutes === undefined || breakData.duration_minutes <= 0) {
      return { valid: false, message: 'Break duration must be greater than 0 minutes' };
    }
    
    // Validate that end time is after start time if in time mode
    if (!isTimeInputMode && endDateTime && startDateTime && new Date(endDateTime) <= new Date(startDateTime)) {
      return { valid: false, message: 'End time must be after start time' };
    }
    
    // Validate that duration isn't too long (greater than 8 hours)
    if (breakData.duration_minutes > 480) {
      return { valid: false, message: 'Break duration cannot exceed 8 hours (480 minutes)' };
    }
    
    return { valid: true };
  };
  
  // Format a date for display in the form
  const formatDateDisplay = (date: Date): string => {
    return date.toLocaleString('en-US', {
      day: 'numeric', 
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  // Initialize datetime values
  useEffect(() => {
    // Set current date/time as default values (in ISO format)
    const now = new Date();
    const later = new Date(now.getTime() + 30 * 60000); // 30 minutes later
    
    setStartDateTime(now.toISOString().slice(0, 16));
    setEndDateTime(later.toISOString().slice(0, 16));
  }, []);

  // Function to handle duration changes
  const handleDurationChange = (minutes: number) => {
    // Ensure minutes is within valid range (1 to 480 = 8 hours)
    const validMinutes = Math.min(Math.max(1, minutes), 480);
    
    // Update the break object
    setNewBreak({
      ...newBreak,
      duration_minutes: validMinutes
    });
    
    // Update the end time based on the new duration
    if (startDateTime) {
      const startMs = new Date(startDateTime).getTime();
      const newEndTime = new Date(startMs + validMinutes * 60000);
      setEndDateTime(newEndTime.toISOString().slice(0, 16));
    }
  };

  const handleAddBreak = () => {
    const validation = validateBreak(newBreak);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    const updatedBreaks = [...breaks];
    
    if (editingIndex !== null) {
      // Update existing break
      updatedBreaks[editingIndex] = newBreak;
    } else {
      // Add new break
      updatedBreaks.push(newBreak);
    }

    // Save and return to timesheet entry
    navigate(returnPath, {
      state: {
        ...location.state,
        updatedBreaks: updatedBreaks,
      },
      replace: true
    });
  };

  const handleDeleteBreak = (index: number) => {
    const updatedBreaks = breaks.filter((_, i) => i !== index);
    setBreaks(updatedBreaks);
  };

  const handleEditBreak = (index: number) => {
    setNewBreak({ ...breaks[index] });
    
    // Calculate end time based on start time + duration
    const now = new Date();
    const duration = breaks[index].duration_minutes || 30;
    const later = new Date(now.getTime() + duration * 60000);
    
    setStartDateTime(now.toISOString().slice(0, 16));
    setEndDateTime(later.toISOString().slice(0, 16));
    setEditingIndex(index);
  };

  const formatBreakTime = (breakData: BreakDuration): string => {
    if (breakData.duration_minutes !== undefined) {
      const hours = Math.floor(breakData.duration_minutes / 60);
      const minutes = breakData.duration_minutes % 60;
      
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h`;
      } else {
        return `${minutes}m`;
      }
    }
    return '';
  };

  // Monitor changes to detect modifications
  useEffect(() => {
    if (initialBreaks) {
      // Check if current breaks are different from initial breaks
      if (JSON.stringify(breaks) !== JSON.stringify(initialBreaks)) {
        setBreakHasChanged(true);
      }
    }
  }, [breaks, initialBreaks]);
  
  const handleSaveAndReturn = () => {
    // Navigate back with updated breaks
    navigate(returnPath, {
      state: {
        ...location.state,
        updatedBreaks: breaks,
      },
      replace: true
    });
    
    // Reset change detection
    setBreakHasChanged(false);
  };
  
  // Handle back button and prompt for unsaved changes
  const handleBack = () => {
    const currentBreak = (editingIndex !== null && initialBreaks) ? initialBreaks[editingIndex] : null;
    const hasChanges = currentBreak ? 
      (currentBreak.type !== newBreak.type || currentBreak.duration_minutes !== newBreak.duration_minutes) :
      true; // New break always counts as a change
      
    if (hasChanges) {
      if (window.confirm("You have unsaved changes. Are you sure you want to go back without saving?")) {
        navigate(returnPath, { state: { ...location.state, updatedBreaks: undefined }});
      }
    } else {
      navigate(returnPath, { state: { ...location.state, updatedBreaks: undefined }});
    }
  };

  // Handlers for datetime inputs
  const handleStartDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDateTime = e.target.value;
    setStartDateTime(newStartDateTime);
    
    // Update duration based on new start time
    if (endDateTime) {
      const startMs = new Date(newStartDateTime).getTime();
      const endMs = new Date(endDateTime).getTime();
      const durationMs = endMs - startMs;
      const durationMinutes = Math.max(1, Math.round(durationMs / 60000));
      
      setNewBreak({
        ...newBreak,
        duration_minutes: durationMinutes
      });
    }
    
    setBreakHasChanged(true);
  };
  
  const handleEndDateTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDateTime = e.target.value;
    setEndDateTime(newEndDateTime);
    
    // Update duration based on new end time
    if (startDateTime) {
      const startMs = new Date(startDateTime).getTime();
      const endMs = new Date(newEndDateTime).getTime();
      const durationMs = endMs - startMs;
      const durationMinutes = Math.max(1, Math.min(480, Math.round(durationMs / 60000)));
      
      setNewBreak({
        ...newBreak,
        duration_minutes: durationMinutes
      });
    }
    
    setBreakHasChanged(true);
  };

  return (
    <div className="px-4 py-3">

      {/* Toggle for input mode - using same labels as AddTimesheetEntry */}
      <div className="mb-4 flex justify-between items-center">
        <label className="text-sm font-medium">Input Mode</label>
        <div className="flex items-center">
          <span className={`mr-2 text-sm ${!isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Duration</span>
          <button
            type="button"
            role="switch"
            aria-checked={isTimeInputMode}
            onClick={() => setIsTimeInputMode(!isTimeInputMode)}
            className={`relative inline-flex items-center h-5 rounded-full w-10 transition-colors ${
              isTimeInputMode ? "bg-blue-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                isTimeInputMode ? "translate-x-5" : "translate-x-1"
              }`}
            />
          </button>
          <span className={`ml-2 text-sm ${isTimeInputMode ? 'font-medium text-blue-600' : 'text-gray-500'}`}>Hours</span>
        </div>
      </div>

      {isTimeInputMode ? (
        <>
          {/* Hours-based input mode */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Duration (minutes)</label>
            <input 
              type="number"
              min="1"
              max="480"
              value={newBreak.duration_minutes}
              onChange={(e) => handleDurationChange(parseInt(e.target.value) || 30)}
              className="w-full p-2 border border-gray-300 rounded-md text-sm"
            />
          </div>

          {/* Duration quick selection - only shown in hours mode */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Quick Select</label>
            <div className="grid grid-cols-4 gap-2">
              <button
                type="button"
                onClick={() => handleDurationChange(15)} 
                className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 15 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
              >
                15 min
              </button>
              <button
                type="button"
                onClick={() => handleDurationChange(30)}
                className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 30 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
              >
                30 min
              </button>
              <button
                type="button"
                onClick={() => handleDurationChange(45)}
                className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 45 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
              >
                45 min
              </button>
              <button
                type="button"
                onClick={() => handleDurationChange(60)}
                className={`px-2 py-1 border rounded-md text-sm ${newBreak.duration_minutes === 60 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300'}`}
              >
                60 min
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Time-based input mode - with editable datetime inputs */}
          <div className="mb-4">
            <label className="block text-md font-medium mb-1">Start Time</label>
            <input
              type="datetime-local"
              value={startDateTime}
              onChange={handleStartDateTimeChange}
              className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
              placeholder="Start Date & Time"
            />
          </div>

          <div className="mb-4">
            <label className="block text-md font-medium mb-1">End Time</label>
            <input
              type="datetime-local"
              value={endDateTime}
              min={startDateTime}
              onChange={handleEndDateTimeChange}
              className="w-full mt-1 p-3 border border-gray-300 rounded-md text-gray-700"
              placeholder="End Date & Time"
            />
          </div>

          <div className="mb-4">
            <div className="flex justify-between items-center">
              <label className="block text-md font-medium">Duration</label>
              <div className="text-md font-medium">
                {newBreak.duration_minutes} min
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-between items-center mb-6">
        <label className="text-sm font-medium">Paid Break</label>
        <button
          type="button"
          role="switch"
          aria-checked={newBreak.type === 'paid'}
          onClick={() => setNewBreak({
            ...newBreak,
            type: newBreak.type === 'paid' ? 'unpaid' : 'paid'
          })}
          className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors ${
            newBreak.type === 'paid' ? "bg-blue-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
              newBreak.type === 'paid' ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-auto">
        <button
          type="button"
          onClick={handleBack}
          className="py-2 bg-gray-400 text-white rounded-md font-medium text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleAddBreak}
          className="py-2 bg-blue-600 text-white rounded-md font-medium text-sm"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default BreakManagerPage;
